import _ from "lodash";
import type { ExtendedMetadataClassifier } from "models/ClassifierModel";
import type { SignalsPersistedObjectFilters } from "components/Signals/SignalsModels";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { isGroupableExtendedMetadataField } from "utils/extendedMetadata";
import {
    getObjectFilterKeysFromFilters,
    isExtendedMetadataObjectFilterKey,
    type ObjectSelectableFilterKey,
} from "./objectFilterKeys";
import {
    clampObjectFiltersToSource,
    compareFiltersByKey,
    getModeCompanionKeyForFilterKey,
    isFilterValueEmpty,
    type FilterComparisonContext,
    type FilterConflictRule,
} from "./filterComparison";

export type PersistedObjectFilterConflict = {
    key: ObjectSelectableFilterKey;
    conflictingValues: unknown[];
    conflictRule: FilterConflictRule;
};

type PersistedObjectFilterState = Pick<
    SignalsPersistedObjectFilters,
    | "objectFilters"
    | "objectFilterDateOverride"
    | "objectFilterDateTypeOverride"
    | "objectFilterCallDurationOverride"
    | "objectFilterMessageCountOverride"
>;

function parseCommaSeparatedValues(value?: string): string[] {
    if (!value) return [];

    return value
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
}

function normalizeExtendedMetadataStringValue(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function normalizeExtendedMetadataGroupedValue(value: unknown):
    | { contains: boolean; values: string[] }
    | undefined {
    if (!value || typeof value !== "object") return undefined;

    const groupedValue = value as {
        contains?: boolean;
        values?: unknown[];
    };
    const normalizedValues = (groupedValue.values ?? [])
        .map((entry) => `${entry}`.trim())
        .filter((entry) => entry.length > 0);

    if (normalizedValues.length === 0) return undefined;

    return {
        contains: groupedValue.contains !== false,
        values: normalizedValues,
    };
}

export function synchronizePersistedObjectFiltersExtendedMetadata(
    persistedObjectFilters: PersistedObjectFilterState,
    extendedMetadataFields: ExtendedMetadataClassifier[],
): {
    persistedObjectFilters: SignalsPersistedObjectFilters;
    changed: boolean;
} {
    const nextPersistedObjectFilters = _.cloneDeep(
        persistedObjectFilters,
    ) as SignalsPersistedObjectFilters;

    if (extendedMetadataFields.length === 0) {
        return {
            persistedObjectFilters: nextPersistedObjectFilters,
            changed: false,
        };
    }

    const currentObjectFilters = nextPersistedObjectFilters.objectFilters;
    const metadataKeys = new Set([
        ...Object.keys(currentObjectFilters.extendedMetadata ?? {}),
        ...Object.keys(currentObjectFilters.extendedMetadataGrouped ?? {}),
    ]);

    const nextExtendedMetadata: Record<string, string> = {};
    const nextExtendedMetadataGrouped: NonNullable<
        ApplicationFilters["extendedMetadataGrouped"]
    > = {};

    metadataKeys.forEach((metadataKey) => {
        const filterKey = `extendedMetadata.${metadataKey}`;
        const isGroupable = isGroupableExtendedMetadataField(
            extendedMetadataFields,
            filterKey,
        );
        const groupedValue = normalizeExtendedMetadataGroupedValue(
            currentObjectFilters.extendedMetadataGrouped?.[metadataKey],
        );
        const stringValue = normalizeExtendedMetadataStringValue(
            currentObjectFilters.extendedMetadata?.[metadataKey],
        );

        if (isGroupable) {
            if (groupedValue) {
                nextExtendedMetadataGrouped[metadataKey] = groupedValue;
                if (groupedValue.contains) {
                    nextExtendedMetadata[metadataKey] =
                        groupedValue.values.join(", ");
                }
                return;
            }

            if (!stringValue) return;

            nextExtendedMetadata[metadataKey] = stringValue;

            const parsedValues = parseCommaSeparatedValues(stringValue);
            if (parsedValues.length === 0) return;

            nextExtendedMetadataGrouped[metadataKey] = {
                contains: true,
                values: parsedValues,
            };
            return;
        }

        if (groupedValue) {
            if (groupedValue.contains) {
                nextExtendedMetadata[metadataKey] =
                    groupedValue.values.join(", ");
            }
            return;
        }

        if (stringValue) {
            nextExtendedMetadata[metadataKey] = stringValue;
        }
    });

    currentObjectFilters.extendedMetadata =
        Object.keys(nextExtendedMetadata).length > 0
            ? nextExtendedMetadata
            : undefined;
    currentObjectFilters.extendedMetadataGrouped =
        Object.keys(nextExtendedMetadataGrouped).length > 0
            ? nextExtendedMetadataGrouped
            : undefined;

    return {
        persistedObjectFilters: nextPersistedObjectFilters,
        changed: !_.isEqual(
            persistedObjectFilters.objectFilters.extendedMetadata,
            currentObjectFilters.extendedMetadata,
        ) ||
            !_.isEqual(
                persistedObjectFilters.objectFilters.extendedMetadataGrouped,
                currentObjectFilters.extendedMetadataGrouped,
            ),
    };
}

function clearObjectFilterValue(
    filters: ApplicationFilters,
    key: ObjectSelectableFilterKey,
) {
    if (key === "wordsAndPhrasesSearchString") {
        delete filters.wordsAndPhrasesSearchString;
        delete filters.wordsAndPhrasesItems;
        delete filters.wordsAndPhrasesIsInputStandard;
        return;
    }

    if (key === "callDuration") {
        filters.minCallDuration = undefined;
        filters.maxCallDuration = undefined;
        return;
    }

    if (key === "messageCount") {
        filters.minMessageCount = undefined;
        filters.maxMessageCount = undefined;
        return;
    }

    if (key === "dateReferenceOption") {
        (filters as any).dateReferenceOption = undefined;
        delete (filters as any).dateReference;
        return;
    }

    if (isExtendedMetadataObjectFilterKey(key)) {
        return;
    }

    delete (filters as any)[key];

    const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
    if (modeCompanionKey) {
        delete (filters as any)[modeCompanionKey];
    }
}

function hasCallDurationValue(filters: ApplicationFilters): boolean {
    return (
        (filters.minCallDuration ?? 0) > 0 || (filters.maxCallDuration ?? 0) > 0
    );
}

function hasMessageCountValue(filters: ApplicationFilters): boolean {
    return (
        (filters.minMessageCount ?? 0) > 0 ||
        (filters.maxMessageCount ?? 0) > 0
    );
}

export function getPersistedObjectFilterConflictsAgainstSource(
    sourceFilters: ApplicationFilters,
    persistedObjectFilters: PersistedObjectFilterState,
    context?: FilterComparisonContext,
): PersistedObjectFilterConflict[] {
    const conflicts: PersistedObjectFilterConflict[] = [];
    const comparisonKeys = getObjectFilterKeysFromFilters(
        persistedObjectFilters.objectFilters,
    );

    if (comparisonKeys.length > 0) {
        const comparison = compareFiltersByKey(
            sourceFilters,
            persistedObjectFilters.objectFilters,
            comparisonKeys,
            context,
        );

        comparison.conflicts.forEach((conflict) => {
            if (!conflict.conflictingValues?.length) return;

            conflicts.push({
                key: conflict.key,
                conflictingValues: conflict.conflictingValues,
                conflictRule: conflict.conflictRule,
            });
        });
    }

    return conflicts;
}

export function resolvePersistedObjectFiltersAgainstSource(
    sourceFilters: ApplicationFilters,
    persistedObjectFilters: PersistedObjectFilterState,
    context?: FilterComparisonContext,
): SignalsPersistedObjectFilters {
    const nextObjectFilters = _.cloneDeep(persistedObjectFilters.objectFilters);
    const comparisonKeys = getObjectFilterKeysFromFilters(nextObjectFilters);
    const clampedObjectFilters =
        comparisonKeys.length > 0
            ? clampObjectFiltersToSource(
                  sourceFilters,
                  nextObjectFilters,
                  comparisonKeys,
                  context,
              )
            : {};
    const conflicts = getPersistedObjectFilterConflictsAgainstSource(
        sourceFilters,
        persistedObjectFilters,
        context,
    );
    const conflictByKey = new Map(
        conflicts.map((conflict) => [conflict.key, conflict]),
    );

    comparisonKeys.forEach((key) => {
        if (isExtendedMetadataObjectFilterKey(key)) return;

        const conflict = conflictByKey.get(key);
        if (conflict?.conflictRule === "remove") {
            clearObjectFilterValue(nextObjectFilters, key);
            return;
        }

        if (key === "callDuration") {
            nextObjectFilters.minCallDuration =
                clampedObjectFilters.minCallDuration;
            nextObjectFilters.maxCallDuration =
                clampedObjectFilters.maxCallDuration;
            return;
        }

        if (key === "messageCount") {
            nextObjectFilters.minMessageCount =
                clampedObjectFilters.minMessageCount;
            nextObjectFilters.maxMessageCount =
                clampedObjectFilters.maxMessageCount;
            return;
        }

        const clampedValue = (clampedObjectFilters as any)[key];
        (nextObjectFilters as any)[key] = clampedValue;

        const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
        if (!modeCompanionKey) return;

        if (isFilterValueEmpty(clampedValue)) {
            delete (nextObjectFilters as any)[modeCompanionKey];
            return;
        }

        const clampedMode = (clampedObjectFilters as any)[modeCompanionKey];
        if (clampedMode !== undefined) {
            (nextObjectFilters as any)[modeCompanionKey] = clampedMode;
        }
    });

    nextObjectFilters.extendedMetadata = _.cloneDeep(
        clampedObjectFilters.extendedMetadata,
    );
    nextObjectFilters.extendedMetadataGrouped = _.cloneDeep(
        clampedObjectFilters.extendedMetadataGrouped,
    );
    nextObjectFilters.selectedCategories = _.cloneDeep(
        clampedObjectFilters.selectedCategories,
    );
    nextObjectFilters.selectedSubcategories = _.cloneDeep(
        clampedObjectFilters.selectedSubcategories,
    );

    const nextDateTypeOverride =
        persistedObjectFilters.objectFilterDateTypeOverride ?? false;
    if (!nextDateTypeOverride) {
        clearObjectFilterValue(nextObjectFilters, "dateReferenceOption");
    }

    const currentCallDurationOverride =
        persistedObjectFilters.objectFilterCallDurationOverride ??
        hasCallDurationValue(persistedObjectFilters.objectFilters);
    const callDurationConflict = conflictByKey.get("callDuration");
    const nextCallDurationOverride =
        callDurationConflict?.conflictRule === "remove"
            ? false
            : currentCallDurationOverride;
    const currentMessageCountOverride =
        persistedObjectFilters.objectFilterMessageCountOverride ??
        hasMessageCountValue(persistedObjectFilters.objectFilters);
    const messageCountConflict = conflictByKey.get("messageCount");
    const nextMessageCountOverride =
        messageCountConflict?.conflictRule === "remove"
            ? false
            : currentMessageCountOverride;

    return {
        objectFilters: nextObjectFilters,
        objectFilterDateOverride:
            persistedObjectFilters.objectFilterDateOverride ?? false,
        objectFilterDateTypeOverride: nextDateTypeOverride,
        objectFilterCallDurationOverride: nextCallDurationOverride,
        objectFilterMessageCountOverride: nextMessageCountOverride,
    };
}
